<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 d-inline-block mb-0">{{ $t("voicemaillog.title") }}</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card
        class="no-border-card"
        body-classes="px-0 pb-1"
        footer-classes="pb-2"
      >
        <template slot="header">
          <div class="row">
            <div class="col-lg-8">
              <h3 class="mb-0">{{ $t("voicemaillog.list_title") }}</h3>
              <p class="text-muted small mb-0">{{ $t("voicemaillog.help") }}</p>
            </div>
            <div class="col-lg-4 text-right">
              <base-button size="sm" type="neutral" @click="search">
                <i class="fa fa-search mr-1"></i>
                {{ $t("voicemaillog.search") }}
              </base-button>
              <!--
                Upload-to-restore, and it is the PRIMARY action on this page —
                Search is demoted to neutral so that "primary" still means one
                thing.

                It sits in the header rather than on a row because it is the
                only restore that does not depend on a row: the API runs on two
                nodes with no shared volume, so a backup's zip is on the disk of
                whichever node produced it and is retired by the keep-newest-N
                sweep besides. The per-row Restore button therefore works only
                while the server still happens to hold that archive; the copy
                the operator downloaded is the one that lasts, and this is the
                way back in for it.
              -->
              <base-button
                size="sm"
                type="primary"
                :disabled="busy || rsOpen"
                @click="openUpload"
              >
                <i class="fa fa-upload mr-1"></i>
                {{ $t("voicemaillog.upload_button") }}
              </base-button>
              <p class="text-muted small mb-0 mt-2">
                {{ $t("voicemaillog.upload_hint") }}
              </p>
            </div>
          </div>
        </template>

        <!--
          Backups and restores are ONE list, filtered, rather than two tabs.
          They are two halves of the same story — this archive was taken, and
          then it was put back — and an operator checking whether a corporate
          was ever restored should not have to know which tab that fact lives
          on. `kind` "" asks the endpoint for both.
        -->
        <div class="px-3 pb-3">
          <div class="form-group row">
            <div class="col-md-4">
              <base-input :label="$t('voicemaillog.filter_kind')">
                <el-select v-model="kind" class="select-primary">
                  <el-option
                    v-for="opt in kindOptions"
                    :key="'k-' + opt.value"
                    :value="opt.value"
                    :label="$t(opt.label)"
                  >
                  </el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-md-5">
              <base-input :label="$t('voicemaillog.filter_corporate')">
                <el-select
                  v-model="enterpriseId"
                  filterable
                  class="select-primary"
                >
                  <el-option
                    :value="0"
                    :label="$t('voicemaillog.corporate_all')"
                  ></el-option>
                  <el-option
                    v-for="corp in mainCorporates"
                    :key="'c-' + corp.ID"
                    :value="corp.ID"
                    :label="corp.Name"
                  >
                  </el-option>
                </el-select>
              </base-input>
            </div>
          </div>
        </div>

        <el-table
          :data="rows"
          header-row-class-name="thead-light"
          v-loading="loading"
        >
          <el-table-column :label="$t('voicemaillog.col_kind')" min-width="110">
            <template slot-scope="{ row }">
              <!-- Plain text at body size, not a badge: the operator found the
                   pale badge unreadable. Black for a backup, orange for a
                   restore, so the two kinds still tell apart at a glance. -->
              <span class="font-weight-bold" :class="kindClass(row.kind)">
                {{ kindText(row.kind) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('voicemaillog.col_corporate')"
            min-width="220"
          >
            <template slot-scope="{ row }">
              <!-- The EAS server is deliberately not shown here: it is the
                   audit-trail value from the row, not the address the worker
                   used, and on this page it only added noise. -->
              <div>{{ row.enterpriseName || row.enterpriseId }}</div>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('voicemaillog.col_created')"
            min-width="170"
          >
            <template slot-scope="{ row }">
              <div>{{ row.createdAt }}</div>
              <small class="text-muted">{{ formatDuration(row.elapsed) }}</small>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('voicemaillog.col_by')"
            prop="createdBy"
            min-width="140"
          ></el-table-column>
          <el-table-column
            :label="$t('voicemaillog.col_numbers')"
            min-width="110"
          >
            <template slot-scope="{ row }">
              {{ row.done }} / {{ row.total }}
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('voicemaillog.col_counts')"
            min-width="150"
          >
            <template slot-scope="{ row }">
              <span class="badge badge-success mr-1">
                {{ $t("voicemaillog.messages_label") }} {{ row.messages }}
              </span>
              <!-- Errors is the number an operator is scanning this column for,
                   so a non-zero count is red and a zero one is not. -->
              <span
                class="badge"
                :class="row.errors > 0 ? 'badge-danger' : 'badge-light'"
              >
                {{ $t("voicemaillog.errors_label") }} {{ row.errors }}
              </span>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('voicemaillog.col_status')"
            min-width="150"
          >
            <template slot-scope="{ row }">
              <span class="badge" :class="statusBadge(row.statusName)">
                {{ statusText(row.statusName) }}
              </span>
              <div>
                <small class="text-muted">{{ phaseText(row.phase) }}</small>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('voicemaillog.col_size')" min-width="110">
            <template slot-scope="{ row }">
              <span v-if="row.hasFile">{{ formatBytes(row.size) }}</span>
              <small v-else class="text-muted">
                {{ $t("voicemaillog.no_file") }}
              </small>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('voicemaillog.col_actions')"
            min-width="260"
          >
            <template slot-scope="{ row }">
              <!--
                Download. Enabled from HasFile, never from the phase: the
                retention sweep keeps only the newest few zips, so a perfectly
                successful backup from last month has no file at all. On a
                BACKUP row the button stays visible and greyed rather than
                disappearing, because "this backup's archive is gone" is a fact
                worth showing. On a RESTORE row it is not rendered at all: a
                restore never produces anything to download, so a greyed button
                there says nothing.
              -->
              <base-button
                v-if="row.kind === 'backup'"
                size="sm"
                type="neutral"
                :disabled="!row.hasFile || downloadingId === row.jobId"
                @click="download(row)"
              >
                <i
                  class="mr-1"
                  :class="
                    downloadingId === row.jobId
                      ? 'fa fa-spinner fa-spin'
                      : 'fa fa-download'
                  "
                ></i>
                {{ $t("voicemaillog.btn_download") }}
              </base-button>

              <!-- The per-number rows. This is where an operator finds out
                   WHICH numbers failed, so it is offered on every job,
                   including a failed one. -->
              <base-button size="sm" type="neutral" @click="openLog(row)">
                <i class="fa fa-list mr-1"></i>
                {{ $t("voicemaillog.btn_log") }}
              </base-button>

              <!-- Restore exists only on a backup row: a restore job has no
                   archive of its own to replay. Greyed when the zip is gone,
                   for the same reason Download is. -->
              <base-button
                v-if="row.kind === 'backup'"
                size="sm"
                type="warning"
                :disabled="!row.hasFile || busy || rsOpen"
                @click="confirmRestore(row)"
              >
                <i class="fa fa-undo mr-1"></i>
                {{ $t("voicemaillog.btn_restore") }}
              </base-button>
            </template>
          </el-table-column>
        </el-table>

        <p v-if="!loading && rows.length === 0" class="text-muted px-3 pt-3">
          {{ $t("voicemaillog.no_rows") }}
        </p>

        <div
          slot="footer"
          class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
          v-if="total > 0"
        >
          <p class="card-category">
            {{ $t("data") }} {{ rangeFrom + 1 }} {{ $t("to") }} {{ rangeTo }}
            {{ $t("of") }} {{ total }}
          </p>
          <base-pagination
            class="pagination-no-border"
            :value="page"
            @input="onPageChange"
            :per-page="perPage"
            :total="total"
          >
          </base-pagination>
        </div>
      </card>
    </div>

    <!-- ------------------------- per-number log -------------------------
         Dismissable, unlike the two progress popups: nothing is running behind
         it, so closing it cannot orphan a timer. -->
    <modal :show.sync="logOpen" modal-classes="modal-lg">
      <template slot="header">
        <h5 class="modal-title">
          {{ $t("voicemaillog.log_title") }}
          <small class="text-muted ml-2" v-if="logJob">
            #{{ logJob.jobId }} · {{ logJob.enterpriseName }}
          </small>
        </h5>
      </template>

      <!--
        The operator arrives here from an amber "the archive is incomplete"
        warning, so this modal has to answer "incomplete how". The three results
        are named in full rather than left to a badge colour, because `no_data`
        is the one nobody has seen before and the one that looks harmless: the
        switch answered, so nothing appears to have gone wrong, and the number
        was still never archived.
      -->
      <p class="text-muted small mb-2">
        {{ $t("voicemaillog.log_legend") }}
      </p>

      <div
        v-if="!loadingLog && hasNoDataRows()"
        class="alert alert-warning py-2 mb-3"
      >
        <i class="fa fa-exclamation-triangle mr-1"></i>
        {{ $t("voicemaillog.log_no_data_note") }}
      </div>

      <p v-if="logRows.length > 0" class="mb-2">
        <span class="badge badge-success mr-1">
          {{ $t("voicemaillog.log_count_read") }} {{ logPageCounts.read }}
        </span>
        <span
          class="badge"
          :class="
            logPageCounts.incomplete > 0 ? 'badge-danger' : 'badge-light'
          "
        >
          {{ $t("voicemaillog.log_count_incomplete") }}
          {{ logPageCounts.incomplete }}
        </span>
      </p>

      <el-table
        :data="logRows"
        header-row-class-name="thead-light"
        v-loading="loadingLog"
      >
        <el-table-column
          :label="$t('voicemaillog.log_col_seq')"
          prop="seq"
          min-width="70"
        ></el-table-column>
        <el-table-column
          :label="$t('voicemaillog.log_col_number')"
          prop="number"
          min-width="140"
        ></el-table-column>
        <el-table-column
          :label="$t('voicemaillog.log_col_action')"
          min-width="110"
        >
          <template slot-scope="{ row }">{{ actionText(row.action) }}</template>
        </el-table-column>
        <el-table-column
          :label="$t('voicemaillog.log_col_result')"
          min-width="110"
        >
          <template slot-scope="{ row }">
            <span class="badge" :class="resultBadge(row.result)">
              {{ resultText(row.result) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('voicemaillog.log_col_messages')"
          prop="messages"
          min-width="110"
        ></el-table-column>
        <!--
          Detail is written by the worker in the `key|human text` form the
          ErrorLog uses, so it goes through errorLogText() rather than straight
          to the screen. Rendered raw it reads
          "errors.voicemailbackup.no_repository|MetaSwitch answered success..."
          — a dotted path in front of the one sentence that explains the row.
        -->
        <el-table-column
          :label="$t('voicemaillog.log_col_detail')"
          min-width="320"
        >
          <template slot-scope="{ row }">
            <small class="text-muted">{{ logDetailText(row.detail) }}</small>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('voicemaillog.log_col_time')"
          prop="createdAt"
          min-width="170"
        ></el-table-column>
      </el-table>

      <p v-if="!loadingLog && logRows.length === 0" class="text-muted pt-3">
        {{ $t("voicemaillog.log_none") }}
      </p>

      <div
        class="d-flex justify-content-center justify-content-sm-between flex-wrap pt-3"
        v-if="logTotal > 0"
      >
        <p class="card-category">
          {{ $t("data") }} {{ logRangeFrom + 1 }} {{ $t("to") }}
          {{ logRangeTo }} {{ $t("of") }} {{ logTotal }}
        </p>
        <base-pagination
          class="pagination-no-border"
          :value="logPage"
          @input="onLogPageChange"
          :per-page="logPerPage"
          :total="logTotal"
        >
        </base-pagination>
      </div>

      <template slot="footer">
        <button
          type="button"
          class="btn btn-link ml-auto"
          @click="closeLog"
        >
          {{ $t("close") }}
        </button>
      </template>
    </modal>

    <!-- --------------------- upload an archive to restore ------------------
         Not dismissable by the backdrop while a body is in flight: closing it
         would drop the reference to the XHR that is still uploading hundreds of
         megabytes and leave the operator with no way to see how it ended.
         Cancel is offered instead and is disabled for exactly as long as `busy`
         is true.

         Errors stay HERE rather than being handed to the progress modal. Every
         refusal this endpoint makes — not a zip, no vm_*.xml inside, the wrong
         corporate, too large — is answered before a job row exists, and the
         fix is to pick a different file or a different corporate and press the
         button again. The progress modal is for a job that exists. -->
    <modal :show="upOpen" :show-close="false">
      <template slot="header">
        <h5 class="modal-title">
          <i class="fa fa-upload mr-2"></i>
          {{ $t("voicemaillog.upload_title") }}
        </h5>
      </template>

      <!-- What the restore actually does, and the one thing the operator has to
           get right. Merge is explained again here rather than left to the
           per-row confirmation, because this entry point never shows that
           confirmation: the file was chosen from a Downloads folder, so there
           is no row naming the corporate and the date to check it against. -->
      <p class="text-muted small">{{ $t("voicemaillog.upload_explain") }}</p>

      <base-input :label="$t('voicemaillog.upload_corporate')">
        <!-- Top-level corporates only, the same list the filter above uses: a
             backup covers a main branch AND its sub-branches, so the main
             branch is the entry that names the archive. -->
        <!-- Opens EMPTY every time, with a placeholder rather than the first
             corporate or the page filter's choice: an operator who does not
             consciously pick the corporate is the one who restores a zip into
             the wrong one. The button stays disabled until a choice is made. -->
        <el-select
          v-model="upEnterpriseId"
          filterable
          clearable
          class="select-primary"
          :placeholder="$t('voicemaillog.upload_corporate_placeholder')"
          :disabled="busy"
        >
          <el-option
            v-for="corp in mainCorporates"
            :key="'u-' + corp.ID"
            :value="corp.ID"
            :label="corp.Name"
          >
          </el-option>
        </el-select>
      </base-input>

      <div class="form-group">
        <label class="form-control-label" for="vm-restore-file">
          {{ $t("voicemaillog.upload_file") }}
        </label>
        <div class="custom-file">
          <input
            id="vm-restore-file"
            ref="uploadFile"
            type="file"
            class="custom-file-input"
            accept=".zip,application/zip"
            lang="en"
            :disabled="busy"
            @change="onUploadFileChange"
          />
          <!-- The chosen file's name AND size. The size is what separates a
               real corporate archive from the ~90-byte JSON error body a failed
               download can leave behind under the same .zip name — a mistake
               this page has already had to guard against once, in download(). -->
          <label class="custom-file-label" for="vm-restore-file">
            {{ uploadFileLabel }}
          </label>
        </div>
      </div>

      <!-- Only while the body is going up. onUploadProgress gives no total on a
           chunked request, and rather than showing a bar that never moves the
           percentage line is simply left out until `e.total` is known. -->
      <div v-if="busy" class="mt-3">
        <base-progress
          type="primary"
          :height="10"
          :value="upPercent"
          :show-label="true"
          :label="$t('voicemaillog.restore_progress_label')"
        >
          <span>{{ upPercent }}%</span>
        </base-progress>
        <p class="text-muted small mb-0 mt-2">
          {{ $t("voicemaillog.upload_uploading", { percent: upPercent }) }}
        </p>
      </div>

      <div v-if="upErrorTitle" class="mt-3">
        <div class="alert alert-danger py-2 mb-2">
          <i class="fa fa-exclamation-triangle mr-1"></i>
          {{ upErrorTitle }}
        </div>
        <pre v-if="upErrorDetail" class="vm-error-detail mb-0">{{ upErrorDetail }}</pre>
      </div>

      <template slot="footer">
        <button
          type="button"
          class="btn btn-link ml-auto"
          :disabled="busy"
          @click="closeUpload"
        >
          {{ $t("cancel") }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!canUpload"
          @click="submitUpload"
        >
          <i
            class="mr-1"
            :class="busy ? 'fa fa-spinner fa-spin' : 'fa fa-upload'"
          ></i>
          {{ $t("voicemaillog.upload_confirm") }}
        </button>
      </template>
    </modal>

    <!-- ------------------------- restore progress -------------------------
         Same discipline as the backup popup on EasMigration.vue: `:show` is
         bound one-way so the backdrop mousedown Modal.vue emits is ignored,
         :show-close is false, and the Close button appears only once the run
         has stopped — because closing is what stops the poll, and a modal that
         could be dismissed mid-run is how a timer outlives the thing it was
         polling for. -->
    <modal :show="rsOpen" :show-close="false">
      <div class="py-2">
        <h4 class="mb-1">
          <i class="mr-2" :class="rsHeadingIcon"></i>
          {{ $t("voicemaillog.restore_title") }}
        </h4>
        <p class="text-muted mb-3" v-if="rsCorporateName">
          {{ rsCorporateName }}
        </p>

        <div class="pass-timeline mb-3">
          <div
            v-for="s in rsSteps"
            :key="'rs-' + s.id"
            class="pass-step"
            :class="s.state"
          >
            <span class="pass-step-name">{{ $t(s.label) }}</span>
            <small class="pass-step-hint">{{ $t(s.hint) }}</small>
          </div>
        </div>

        <base-progress
          :type="rsStage === 'error' ? 'danger' : 'primary'"
          :height="10"
          :value="rsPercent"
          :show-label="true"
          :label="$t('voicemaillog.restore_progress_label')"
        >
          <span>{{ rs.done }} / {{ rs.total }}</span>
        </base-progress>

        <!-- Only while the write is running: once it is over, CurrentNumber is
             whatever the worker happened to stop on, and leaving it on screen
             would read as "still stuck there". -->
        <p class="mb-1 mt-3" v-if="rsStage === 'restore'">
          <strong>{{ $t("voicemaillog.restore_current_number") }}:</strong>
          {{ rs.current || $t("voicemaillog.restore_current_none") }}
        </p>

        <p class="mb-1" :class="rsStage === 'restore' ? '' : 'mt-3'">
          <strong>{{ $t("voicemaillog.restore_elapsed") }}:</strong>
          {{ formatDuration(rs.elapsed) }}
        </p>

        <p class="mb-0">
          <span class="badge badge-success mr-1">
            {{ $t("voicemaillog.messages_label") }} {{ rs.messages }}
          </span>
          <span
            class="badge"
            :class="rs.errors > 0 ? 'badge-danger' : 'badge-light'"
          >
            {{ $t("voicemaillog.errors_label") }} {{ rs.errors }}
          </span>
        </p>

        <!-- Finished, but not every mailbox was merged. The same warning the
             backup modal gives, and for the same reason: "restore complete"
             with a non-zero error count is how a corporate is believed to be
             whole when it is not. The wording underneath says the archive was
             not fully restored WITHOUT implying anything was lost — merge takes
             nothing away, so the honest reading is "run it again", and it says
             so. -->
        <div
          v-if="rsStage === 'done' && rs.errors > 0"
          class="alert alert-warning py-2 mt-3 mb-0"
        >
          <i class="fa fa-exclamation-triangle mr-1"></i>
          {{ $t("voicemaillog.restore_partial") }}
        </div>

        <div
          v-if="rsStage === 'done' && rs.errors === 0"
          class="alert alert-success py-2 mt-3 mb-0"
        >
          <i class="fa fa-check mr-1"></i>
          {{ $t("voicemaillog.restore_done_text") }}
        </div>

        <div v-if="rsStage === 'error'" class="mt-3">
          <div class="alert alert-danger py-2 mb-2">
            <i class="fa fa-exclamation-triangle mr-1"></i>
            {{ rsErrorTitle || $t("voicemaillog.restore_error_title") }}
          </div>
          <pre v-if="rsErrorDetail" class="vm-error-detail mb-2">{{ rsErrorDetail }}</pre>
          <div v-if="rs.errorLog">
            <p class="mb-1">
              <strong>{{ $t("voicemaillog.restore_error_log_title") }}</strong>
            </p>
            <pre class="vm-error-detail">{{ rsErrorLogText(rs.errorLog) }}</pre>
          </div>
        </div>

        <!-- The per-number rows of the restore itself, once there are any to
             look at. -->
        <p v-if="rsFinished && rsJobId > 0" class="text-muted small mt-3 mb-0">
          {{ $t("voicemaillog.restore_see_log") }}
        </p>
      </div>

      <template slot="footer">
        <button
          v-if="rsFinished"
          type="button"
          class="btn btn-link ml-auto"
          @click="closeRestore"
        >
          {{ $t("close") }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import Modal from "@/components/Modal";
import { BasePagination } from "@/components";
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";
import {
  describeExportError,
  downloadBlob,
  isJsonBody,
  readBlobAsJSON
} from "./exportMixin";
// The wire contract, shared with EasMigration.vue. Field names, phase names
// and the normalisers live there and nowhere else, so a rename on the backend
// is one line in one file for both pages.
import {
  VM_FIELDS,
  VM_KIND_BACKUP,
  VM_KIND_RESTORE,
  VM_PHASE_DONE,
  VM_PHASE_ERROR,
  VM_PHASE_EXTRACT,
  VM_PHASE_RESTORE,
  VM_POLL_MS,
  VM_RESULT_NO_DATA,
  VM_RESULT_OK,
  VM_STATUS_FAILED,
  VM_ZIP_MIME,
  formatVmBytes,
  formatVmDuration,
  normaliseVmJob,
  normaliseVmLogRow,
  vmErrorKeyForKind,
  vmNum,
  vmResultIsRead
} from "./voicemailJob";
import { WebPortalService } from "../../resource";

const PER_PAGE = 20;
const LOG_PER_PAGE = 50;

// The restore's three steps, in order. Unlike the migration's passes these ARE
// the backend's phases one for one: extract -> restore -> done.
const RESTORE_STEPS = [
  {
    id: VM_PHASE_EXTRACT,
    label: "voicemaillog.restore_step_extract",
    hint: "voicemaillog.restore_step_extract_hint"
  },
  {
    id: VM_PHASE_RESTORE,
    label: "voicemaillog.restore_step_restore",
    hint: "voicemaillog.restore_step_restore_hint"
  },
  {
    id: VM_PHASE_DONE,
    label: "voicemaillog.restore_step_done",
    hint: "voicemaillog.restore_step_done_hint"
  }
];
const RESTORE_STEP_ORDER = RESTORE_STEPS.map(s => s.id);

// Phase, status, kind, action and result all arrive as bare English words, and
// every one of them is shown to an operator reading a Thai screen. Each is
// looked up in a table rather than translated inline, so a value the backend
// adds later falls through to the raw word — which is readable — instead of a
// dotted key path, which is not.
const PHASE_KEYS = {
  read: "voicemaillog.phase_read",
  compress: "voicemaillog.phase_compress",
  ready: "voicemaillog.phase_ready",
  extract: "voicemaillog.phase_extract",
  restore: "voicemaillog.phase_restore",
  done: "voicemaillog.phase_done",
  error: "voicemaillog.phase_error"
};
const STATUS_KEYS = {
  queued: "voicemaillog.status_queued",
  running: "voicemaillog.status_running",
  ready: "voicemaillog.status_ready",
  failed: "voicemaillog.status_failed"
};
const KIND_KEYS = {
  backup: "voicemaillog.kind_backup",
  restore: "voicemaillog.kind_restore"
};
const ACTION_KEYS = {
  read: "voicemaillog.action_read",
  restore: "voicemaillog.action_restore"
};
// Three results, not two. `no_data` is a state of its own: the switch answered,
// but nothing was archived for that number. It must not read as success and
// must not be dressed as a failure to reach the switch — see voicemailJob.js.
const RESULT_KEYS = {
  ok: "voicemaillog.result_ok",
  error: "voicemaillog.result_error",
  no_data: "voicemaillog.result_no_data"
};

export default {
  name: "voicemail-backup-log",
  components: {
    RouteBreadCrumb,
    Modal,
    BasePagination,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn
  },
  data() {
    return {
      // "" asks for both kinds. 0 asks for every corporate.
      kind: "",
      enterpriseId: 0,
      kindOptions: [
        { value: "", label: "voicemaillog.kind_all" },
        { value: VM_KIND_BACKUP, label: "voicemaillog.kind_backup" },
        { value: VM_KIND_RESTORE, label: "voicemaillog.kind_restore" }
      ],

      rows: [],
      total: 0,
      page: 1,
      perPage: PER_PAGE,
      loading: false,
      // Guards the mutating button only. A download or a log read may happen
      // while a restore is being started; a second restore may not.
      busy: false,
      // Which row's zip is being fetched, so only that button spins.
      downloadingId: 0,

      logOpen: false,
      logJob: null,
      logRows: [],
      logTotal: 0,
      logPage: 1,
      logPerPage: LOG_PER_PAGE,
      loadingLog: false,

      // ------------------------ upload to restore ------------------------
      // The archive the operator downloaded earlier, on its way back in. No
      // flag of its own for "an upload is in flight" — `busy` above is that
      // flag, which is what keeps the per-row Restore buttons and this modal's
      // own confirm from being pressed while a body is going up.
      upOpen: false,
      upEnterpriseId: null,
      // The File object itself, not the input element: the element is reset on
      // close so that re-choosing the SAME file fires `change` again.
      upFile: null,
      // 0-100 from onUploadProgress, and only when the request knows its total.
      upPercent: 0,
      upErrorTitle: "",
      upErrorDetail: "",

      // ---------------------------- restore ----------------------------
      // rsOpen doubles as the "a restore is running" flag: the popup cannot be
      // dismissed until the run finishes, so it is open for exactly as long as
      // there is a run to guard against.
      rsOpen: false,
      rsJobId: 0,
      rsCorporateName: "",
      // "" | "extract" | "restore" | "done" | "error"
      rsStage: "",
      rsErrorStep: "",
      rsErrorTitle: "",
      rsErrorDetail: "",
      rs: normaliseVmJob(null),
      rsPollTimer: null,

      // Set in beforeDestroy. An in-flight /job/status can resolve after the
      // component is gone; without this it would schedule another poll.
      killed: false
    };
  },
  computed: {
    ...mapGetters({
      corporates: "CorporateV4/getData"
    }),
    // Only a main branch owns an EAS server, so only a main branch has its own
    // voicemail jobs; a sub-branch is backed up with its parent.
    mainCorporates() {
      return (this.corporates || []).filter(c => c && !c.UpperID);
    },
    rangeFrom() {
      return (this.page - 1) * this.perPage;
    },
    // Name and size of the chosen archive, or the placeholder. The size is
    // shown because it is the cheapest way to notice the wrong file: a real
    // corporate archive is tens or hundreds of megabytes, and a truncated or
    // stray download that kept the .zip name is not.
    uploadFileLabel() {
      if (!this.upFile) return this.$t("voicemaillog.upload_file_placeholder");
      return this.upFile.name + " (" + formatVmBytes(this.upFile.size) + ")";
    },
    // Both halves chosen, and nothing already running. rsOpen is in here as
    // well as `busy` for the same reason confirmRestore tests it: the progress
    // modal is single-tenant, and a second job started behind it would be
    // polled by nobody.
    canUpload() {
      if (this.busy || this.rsOpen) return false;
      return Number(this.upEnterpriseId) > 0 && this.upFile !== null;
    },
    rangeTo() {
      const to = this.rangeFrom + this.rows.length;
      return to > this.total ? this.total : to;
    },
    // Read vs not-read for the rows on screen, counted through the same
    // predicate the backend uses (vmResultIsRead / VmLogResultIsReadV4), so the
    // page cannot drift from ErrorCount on what "read" means.
    logPageCounts() {
      const read = this.logRows.filter(r => vmResultIsRead(r.result)).length;
      return { read: read, incomplete: this.logRows.length - read };
    },
    logRangeFrom() {
      return (this.logPage - 1) * this.logPerPage;
    },
    logRangeTo() {
      const to = this.logRangeFrom + this.logRows.length;
      return to > this.logTotal ? this.logTotal : to;
    },

    // The run has stopped, one way or the other. This is what unlocks the
    // Close button, and closing is what stops the poll — so a stage that is not
    // in this list must never reach the footer.
    rsFinished() {
      return this.rsStage === VM_PHASE_DONE || this.rsStage === VM_PHASE_ERROR;
    },
    rsHeadingIcon() {
      if (this.rsStage === VM_PHASE_ERROR) return "fa fa-times-circle text-danger";
      if (this.rsStage === VM_PHASE_DONE) return "fa fa-check-circle text-success";
      return "fa fa-spinner fa-spin text-primary";
    },
    rsPercent() {
      if (this.rsStage === VM_PHASE_DONE) return 100;
      if (!this.rs.total || this.rs.total <= 0) return 0;
      const pct = Math.round((this.rs.done / this.rs.total) * 100);
      if (pct < 0) return 0;
      return pct > 100 ? 100 : pct;
    },
    // Each step marked done / current / failed / ahead. On error the failed
    // step keeps its position and the ones before it stay done, so the strip
    // still says how far the run got.
    rsSteps() {
      const active =
        this.rsStage === VM_PHASE_ERROR ? this.rsErrorStep : this.rsStage;
      const at = RESTORE_STEP_ORDER.indexOf(active);
      return RESTORE_STEPS.map((s, i) => {
        let state = "pass-ahead";
        if (at >= 0) {
          if (i < at) state = "pass-done";
          else if (i === at) {
            state =
              this.rsStage === VM_PHASE_ERROR ? "pass-failed" : "pass-current";
          }
        }
        return { id: s.id, label: s.label, hint: s.hint, state: state };
      });
    }
  },
  mounted() {
    this.listCorporates();
    this.load();
  },
  beforeDestroy() {
    // `killed` stops an already-dispatched request from scheduling the next
    // poll when it resolves. It is set here and in no other place.
    this.killed = true;
    this.stopRestorePolling();
    this.rsOpen = false;
    this.logOpen = false;
    this.upOpen = false;
    // Modal.vue puts `modal-open` on <body> while it is shown and takes it off
    // in a watcher. Navigating away with a popup open destroys the modal before
    // that watcher can fire, which would leave the next page unable to scroll.
    if (typeof document !== "undefined") {
      document.body.classList.remove("modal-open");
    }
  },
  methods: {
    ...mapActions({
      listCorporates: "CorporateV4/listData"
    }),

    // ------------------------------ labels ------------------------------
    //
    // Each of these falls back to the raw wire word for a value it does not
    // know. A value the backend adds later then reads as "sealing" rather than
    // as "voicemaillog.phase_sealing", which is the difference between an
    // unfamiliar word and a visible bug.
    lookupText(table, value) {
      const key = table[value];
      return key ? this.$t(key) : value || "";
    },
    phaseText(phase) {
      return this.lookupText(PHASE_KEYS, phase);
    },
    statusText(status) {
      return this.lookupText(STATUS_KEYS, status);
    },
    kindText(kind) {
      return this.lookupText(KIND_KEYS, kind);
    },
    actionText(action) {
      return this.lookupText(ACTION_KEYS, action);
    },
    resultText(result) {
      return this.lookupText(RESULT_KEYS, result);
    },
    kindClass(kind) {
      return kind === VM_KIND_RESTORE ? "text-warning" : "text-dark";
    },
    statusBadge(status) {
      if (status === "ready") return "badge-success";
      if (status === VM_STATUS_FAILED) return "badge-danger";
      if (status === "running") return "badge-info";
      return "badge-light";
    },
    // Three badges, deliberately three colours.
    //
    // `no_data` gets a class of its own rather than borrowing badge-danger or
    // badge-warning. Sharing badge-danger would say the switch could not be
    // reached, which is false and sends the operator hunting the wrong fault;
    // sharing the kind column's badge-warning would make two unrelated things
    // look alike on the same row. .badge-no-data is defined in this file's
    // scoped styles, following the .badge-read-ok precedent on the migration
    // page — a third state that must not collapse into either neighbour.
    resultBadge(result) {
      if (result === VM_RESULT_OK) return "badge-success";
      if (result === VM_RESULT_NO_DATA) return "badge-no-data";
      return "badge-danger";
    },
    // Whether the page of rows on screen contains any number the switch
    // answered for but archived nothing from. Drives the callout above the
    // table, because this is the one result an operator is unlikely to have
    // seen before and the one most likely to be waved past.
    hasNoDataRows() {
      return this.logRows.some(r => r.result === VM_RESULT_NO_DATA);
    },
    formatDuration(seconds) {
      return formatVmDuration(seconds);
    },
    formatBytes(bytes) {
      return formatVmBytes(bytes);
    },
    // error_log is written as "errors.<key>|<detail>" — the key is translated
    // and the detail appended verbatim.
    // `kind` picks the wording for the one backend key that serves both halves
    // — errors.voicemailbackup.partial means a read failure on a backup and a
    // write failure on a restore. Every other key passes through untouched.
    errorLogText(raw, kind) {
      if (!raw) return "";
      const cut = raw.indexOf("|");
      if (cut < 0) return raw;
      const key = vmErrorKeyForKind(raw.substring(0, cut), kind);
      const detail = raw.substring(cut + 1);
      const translated = this.$t(key);
      return (translated === key ? key : translated) + " " + detail;
    },
    // The per-number Detail column. The Kind comes from the job the modal is
    // open on, so a backup's rows and a restore's rows are worded for what each
    // actually did.
    logDetailText(raw) {
      return this.errorLogText(raw, this.logJob ? this.logJob.kind : "");
    },
    // The restore popup's ErrorLog. Always a restore, by construction.
    rsErrorLogText(raw) {
      return this.errorLogText(raw, VM_KIND_RESTORE);
    },

    // ------------------------------ errors ------------------------------
    showError(err, fallbackKey) {
      const data = (err && err.response && err.response.data) || {};
      const msg = data.Msg || fallbackKey || "voicemaillog.list_failed";
      const translated = this.$t(msg);
      swal.fire({
        title: translated === msg ? msg : translated,
        text: data.Cause || this.getErrorText(data) || (err && err.message) || "",
        icon: "error",
        confirmButtonClass: "btn btn-danger btn-fill",
        buttonsStyling: false
      });
    },
    describeError(err, fallbackKey) {
      return describeExportError(
        err,
        msg => this.$t(msg),
        this.$t(fallbackKey)
      );
    },

    // ------------------------------- list -------------------------------
    search() {
      this.page = 1;
      this.load();
    },
    onPageChange(page) {
      this.page = page;
      this.load();
    },
    async load() {
      this.loading = true;
      try {
        const resp = await WebPortalService.VoicemailJobListV4({
          kind: this.kind,
          enterprise_id: this.enterpriseId || 0,
          page: this.page,
          per_page: this.perPage
        });
        if (this.killed) return;
        const data = (resp.data && resp.data.Data) || [];
        this.rows = data.map(normaliseVmJob);
        this.total = vmNum(resp.data && resp.data.Total);
      } catch (err) {
        if (this.killed) return;
        this.rows = [];
        this.total = 0;
        this.showError(err, "voicemaillog.list_failed");
      } finally {
        this.loading = false;
      }
    },

    // ----------------------------- download -----------------------------
    async download(row) {
      // hasFile, never the phase: the retention sweep deletes the oldest zips
      // while their jobs stay `ready` in the database for ever.
      if (!row || !row.hasFile || this.downloadingId > 0) return;
      this.downloadingId = row.jobId;

      let resp;
      try {
        resp = await WebPortalService.VoicemailJobDownloadV4({
          job_id: row.jobId
        });
        // A JSON error body delivered as a Blob on a 200. Without this check it
        // is saved as a ~90-byte "zip" the operator only discovers is broken
        // later — which, for a pre-migration archive, is far too late.
        if (isJsonBody(resp)) {
          const parsed = await readBlobAsJSON(resp.data);
          const jsonError = new Error(
            "voicemail job download returned a JSON error body"
          );
          jsonError.response = { data: parsed };
          throw jsonError;
        }
      } catch (err) {
        if (this.killed) return;
        // describeError, not showError: responseType is "blob" on this call, so
        // the server's JSON error body arrives as a Blob. showError would read
        // Msg off the Blob, find nothing, and show the bare fallback instead of
        // the reason — which, when the reason is "the file is gone", is the
        // whole of what the operator needed to know.
        const e = await this.describeError(err, "voicemaillog.download_failed");
        if (this.killed) return;
        this.downloadingId = 0;
        swal.fire({
          title: e.title,
          text: e.detail,
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false
        });
        return;
      }
      if (this.killed) return;

      // Content-Disposition normally names the file; FileName is the server's
      // own name for it, and the id-based fallback is last because a Thai
      // corporate name sanitises down to a row of underscores.
      downloadBlob(
        resp,
        row.fileName || "voicemail-backup-" + row.jobId + ".zip",
        VM_ZIP_MIME
      );
      this.downloadingId = 0;
    },

    // ------------------------------- log --------------------------------
    openLog(row) {
      this.logJob = row;
      this.logRows = [];
      this.logTotal = 0;
      this.logPage = 1;
      this.logOpen = true;
      this.loadLog();
    },
    closeLog() {
      this.logOpen = false;
    },
    onLogPageChange(page) {
      this.logPage = page;
      this.loadLog();
    },
    async loadLog() {
      if (!this.logJob) return;
      const jobId = this.logJob.jobId;
      this.loadingLog = true;
      try {
        const resp = await WebPortalService.VoicemailJobLogV4({
          job_id: jobId,
          page: this.logPage,
          per_page: this.logPerPage
        });
        if (this.killed) return;
        // The modal may have been closed, or reopened on a different job,
        // while this was in flight. Writing the answer in either case would
        // show one job's numbers under another job's heading.
        if (!this.logOpen || !this.logJob || this.logJob.jobId !== jobId) return;
        const data = (resp.data && resp.data.Data) || [];
        this.logRows = data.map(normaliseVmLogRow);
        this.logTotal = vmNum(resp.data && resp.data.Total);
      } catch (err) {
        if (this.killed) return;
        this.logRows = [];
        this.logTotal = 0;
        this.showError(err, "voicemaillog.log_failed");
      } finally {
        this.loadingLog = false;
      }
    },

    // ----------------------------- restore ------------------------------
    //
    // A restore MERGES the archived messages into the mailboxes on the switch.
    // It does not overwrite: go-msw v1.0.30's SubscriberMergeVoicemail carries
    // the messages already in the mailbox back up alongside the archived ones,
    // de-duplicated on SentDate + CallerNumber + CalledNumber + SHA-256 of the
    // recording. Messages that arrived since the backup are kept, archived
    // messages already present are not duplicated, and running the same restore
    // twice changes nothing.
    //
    // Merge was chosen over a guard that refuses to restore into a non-empty
    // mailbox precisely so that restoring into a LIVE mailbox is possible. A
    // confirmation that says the operation destroys what is there would shut
    // that door again by frightening people off the case it was built for, so
    // the wording below says what it does rather than reaching for the strongest
    // warning available.

    // It still NAMES THE CORPORATE and the backup date. This page lists every
    // job on the install, one row per line, and the rows differ only by a date
    // and a name — pressing Restore on the row above the one you meant is the
    // easy mistake here, and being told in words whose mailboxes are about to
    // be touched is the only thing that catches it. Merge makes a mis-click
    // recoverable, not harmless: the wrong corporate still gets messages it
    // never had.
    async confirmRestore(row) {
      if (!row || row.kind !== VM_KIND_BACKUP || !row.hasFile) return;
      if (this.busy || this.rsOpen) return;

      const confirmed = await swal.fire({
        title: this.$t("voicemaillog.restore_confirm_title"),
        text: this.$t("voicemaillog.restore_confirm_text", {
          corporate: row.enterpriseName || String(row.enterpriseId),
          created: row.createdAt
        }),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("voicemaillog.restore_confirm_yes"),
        cancelButtonText: this.$t("cancel"),
        confirmButtonClass: "btn btn-warning btn-fill",
        cancelButtonClass: "btn btn-secondary btn-fill",
        buttonsStyling: false
      });
      if (!confirmed.value) return;
      await this.startRestore(row);
    },

    async startRestore(row) {
      this.busy = true;
      this.openRestoreProgress(row.enterpriseName || String(row.enterpriseId));

      try {
        // job_id names the COMPLETED BACKUP whose zip is replayed. The restore
        // gets a JobId of its own, and that is the one polled below.
        const resp = await WebPortalService.VoicemailRestoreStartV4({
          job_id: row.jobId
        });
        if (this.killed) return;
        this.trackStartedRestore(resp);
      } catch (err) {
        if (this.killed) return;
        const e = await this.describeError(err, "voicemaillog.restore_failed");
        if (this.killed) return;
        this.failRestore(VM_PHASE_EXTRACT, e.title, e.detail);
      } finally {
        this.busy = false;
      }
    },

    // The two halves of "a restore has been started", split out so the upload
    // entry point shares them rather than growing a second progress modal.
    // There is one modal and one poll loop on this page, and both restore
    // sources — a server-held archive and an uploaded one — produce the same
    // ResVmJobStartV4 envelope and the same /job/status stream afterwards.

    // Reset every rs* field and open the popup. Called BEFORE the request on
    // the row path (the popup is the acknowledgement that the click landed) and
    // AFTER it on the upload path, where the acknowledgement is the upload
    // modal's own progress bar and a refusal must not leave an empty progress
    // popup on screen.
    openRestoreProgress(corporateName) {
      this.rsJobId = 0;
      this.rsCorporateName = corporateName;
      this.rsErrorStep = "";
      this.rsErrorTitle = "";
      this.rsErrorDetail = "";
      this.rs = normaliseVmJob(null);
      this.rsStage = VM_PHASE_EXTRACT;
      this.rsOpen = true;
    },
    // Take the new job's id off the start envelope and begin polling it.
    trackStartedRestore(resp) {
      this.rsJobId = vmNum(resp.data && resp.data[VM_FIELDS.jobId]);
      if (this.rsJobId <= 0) {
        // "ok" without an id leaves nothing to poll. Saying so beats polling
        // an empty id every two seconds forever.
        this.failRestore(
          VM_PHASE_EXTRACT,
          this.$t("voicemaillog.restore_no_job_id"),
          ""
        );
        return;
      }
      this.startRestorePolling();
    },

    // ------------------------ upload to restore -------------------------
    //
    // The server's copy of an archive is scratch. The API runs on two nodes
    // behind nginx with no shared volume, so a backup's zip only exists on the
    // disk of the node that produced it, the keep-newest-N sweep retires it,
    // and it is lost with the container. The copy that lasts is the one the
    // operator downloaded from the backup modal — so this is the entry point
    // that actually works weeks later, and the per-row Restore button is the
    // one that only works while the file happens to still be there.
    //
    // The corporate is chosen by hand rather than read out of the archive
    // because the server must be told what the operator BELIEVES the file is,
    // so that it can disagree: it parses the first archived document and
    // refuses the upload if that subscriber is not one of the chosen
    // corporate's numbers. Restoring one corporate's voicemail into another's
    // mailboxes is the unrecoverable mistake here, and "picked the wrong zip
    // out of Downloads" is how it happens.
    openUpload() {
      if (this.busy || this.rsOpen) return;
      this.upEnterpriseId = null;
      this.upPercent = 0;
      this.upErrorTitle = "";
      this.upErrorDetail = "";
      this.clearUploadFile();
      this.upOpen = true;
    },
    closeUpload() {
      // Never while `busy`: the footer button is disabled then, and closing
      // would abandon an XHR that is still uploading.
      if (this.busy) return;
      this.upOpen = false;
      this.clearUploadFile();
    },
    // Drop the File AND blank the input element. Without the second half the
    // browser keeps the old selection, and choosing the same file again fires
    // no `change` event — so a retry after a refusal would silently upload
    // nothing.
    clearUploadFile() {
      this.upFile = null;
      const input = this.$refs.uploadFile;
      if (input) input.value = "";
    },
    onUploadFileChange(evt) {
      const files = (evt && evt.target && evt.target.files) || [];
      this.upFile = files.length > 0 ? files[0] : null;
      // A new file is a new attempt; the previous refusal is no longer about
      // anything on screen.
      this.upErrorTitle = "";
      this.upErrorDetail = "";
      this.upPercent = 0;
    },

    async submitUpload() {
      if (!this.canUpload) return;
      const file = this.upFile;
      const enterpriseId = this.upEnterpriseId;
      // Read the name now: the popup this hands over to shows the corporate,
      // and by then the upload modal has been reset.
      const corp = this.mainCorporates.find(c => c && c.ID === enterpriseId);
      const corporateName = (corp && corp.Name) || String(enterpriseId);

      this.busy = true;
      this.upPercent = 0;
      this.upErrorTitle = "";
      this.upErrorDetail = "";

      let resp;
      try {
        // enterprise_id FIRST, then file. The handler streams the parts with a
        // MultipartReader instead of buffering the whole body, so it meets
        // them in wire order; it does not depend on this order, but sending
        // the text part first is what lets it refuse a wrong corporate without
        // having read a gigabyte of zip.
        const form = new FormData();
        form.append("enterprise_id", String(enterpriseId));
        form.append("file", file);

        resp = await WebPortalService.VoicemailRestoreUploadV4(form, e => {
          // No total on a chunked request. Leaving the percentage at 0 is
          // honest; inventing one from `loaded` alone is not.
          if (this.killed || !e || !e.total) return;
          this.upPercent = Math.round((100 * e.loaded) / e.total);
        });
      } catch (err) {
        if (this.killed) return;
        const e = await this.describeError(err, "voicemaillog.upload_failed");
        if (this.killed) return;
        this.busy = false;
        this.upErrorTitle = e.title;
        this.upErrorDetail = e.detail;
        return;
      }
      if (this.killed) return;
      this.busy = false;

      // Accepted, but with no job to poll. Kept in THIS modal rather than
      // handed to the progress popup, because there is nothing to show
      // progress for and the operator's next move is to try the upload again.
      const jobId = vmNum(resp.data && resp.data[VM_FIELDS.jobId]);
      if (jobId <= 0) {
        this.upErrorTitle = this.$t("voicemaillog.restore_no_job_id");
        this.upErrorDetail = (resp.data && resp.data.Cause) || "";
        return;
      }

      // Hand over to the existing progress modal and poll loop.
      this.upOpen = false;
      this.clearUploadFile();
      this.openRestoreProgress(corporateName);
      this.trackStartedRestore(resp);
    },

    // Every failure path lands here. The server's own ErrorLog is deliberately
    // NOT touched: it stays in this.rs.errorLog and the popup renders it
    // verbatim, because it is the only record of which mailboxes the merge did
    // not reach.
    failRestore(step, title, detail) {
      this.stopRestorePolling();
      this.rsErrorStep = step || this.rsStage;
      this.rsStage = VM_PHASE_ERROR;
      this.rsErrorTitle = title || this.$t("voicemaillog.restore_error_title");
      this.rsErrorDetail = detail || "";
    },

    startRestorePolling() {
      // Reached only after awaiting a request, so the component may already be
      // gone. `killed` is never cleared here: doing so would un-kill a dead
      // instance and start a self-rescheduling timeout nothing is left to
      // clear.
      if (this.killed) return;
      this.stopRestorePolling();
      this.restorePollOnce();
    },
    stopRestorePolling() {
      if (this.rsPollTimer) {
        clearTimeout(this.rsPollTimer);
        this.rsPollTimer = null;
      }
    },

    // One poll. Every path out of it either stops the loop or schedules exactly
    // one more tick — there is no path that does both and none that does
    // neither.
    async restorePollOnce() {
      // (1) Before firing. The modal may have been closed or the component
      // destroyed since the timer was set.
      if (this.killed || !this.rsOpen || this.rsJobId <= 0) return;

      let data;
      try {
        const resp = await WebPortalService.VoicemailJobStatusV4({
          job_id: this.rsJobId
        });
        data = resp.data && resp.data.Data;
      } catch (err) {
        // (2) The request itself failed.
        if (this.killed) return;
        const e = await this.describeError(err, "voicemaillog.restore_failed");
        if (this.killed) return;
        this.failRestore(this.rsStage, e.title, e.detail);
        return;
      }

      // (3) and (4) The request was in flight while the component was destroyed
      // or the popup was closed. There is nobody left to show the answer to,
      // and scheduling the next poll is exactly the leak this guard exists for.
      if (this.killed || !this.rsOpen) return;

      this.rs = normaliseVmJob(data);

      // (5) The worker said it failed. No title of its own: the server named
      // the failure in ErrorLog, which the popup renders in full underneath.
      if (this.rs.phase === VM_PHASE_ERROR) {
        this.failRestore(this.rsStage, "", "");
        return;
      }
      // (6) The job is marked failed without a phase having said so — a worker
      // killed mid-run leaves exactly that, and a loop watching only Phase
      // would poll it forever.
      if (this.rs.statusName === VM_STATUS_FAILED) {
        this.failRestore(
          this.rsStage,
          this.$t("voicemaillog.restore_worker_stopped"),
          ""
        );
        return;
      }
      // (7) Finished. The list is reloaded because the restore is itself a new
      // row on it, and because the backup row it replayed may have been
      // touched.
      if (this.rs.phase === VM_PHASE_DONE) {
        this.stopRestorePolling();
        this.rsStage = VM_PHASE_DONE;
        this.load();
        return;
      }

      // Still running — the only path that schedules another tick.
      this.rsStage =
        this.rs.phase === VM_PHASE_RESTORE ? VM_PHASE_RESTORE : VM_PHASE_EXTRACT;
      this.rsPollTimer = setTimeout(this.restorePollOnce, VM_POLL_MS);
    },

    // Closing is what stops the poll. It is only reachable once the run has
    // finished — the popup has no backdrop dismiss and no Close button before
    // then — but stopRestorePolling() is called unconditionally rather than
    // behind a stage test, because "a timer outlived its modal" is the bug, and
    // a guard that happens to be right today is not the same as one that
    // cannot be wrong.
    closeRestore() {
      this.stopRestorePolling();
      this.rsOpen = false;
    }
  }
};
</script>

<style scoped>
/* The step strip, matching the one EasMigration.vue draws for a migration and
   for a backup. Repeated rather than shared because both files scope their
   styles, and an unscoped rule for a class this generic would leak into every
   page in the app. */
.pass-timeline {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.pass-step {
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: #f6f9fc;
  padding: 6px 8px;
  /* grid items default to min-width:auto and would refuse to shrink below
     their longest word, pushing the modal into a horizontal scroll. */
  min-width: 0;
}
.pass-step-name {
  display: block;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
  color: #525f7f;
  overflow-wrap: break-word;
}
.pass-step-hint {
  display: block;
  font-size: 10px;
  line-height: 1.3;
  color: #8898aa;
  overflow-wrap: break-word;
}
.pass-step.pass-done {
  opacity: 0.6;
}
.pass-step.pass-current {
  border-width: 2px;
  border-color: #5e72e4;
  padding: 5px 7px;
}
/* The step a run stopped on. Same weight as .pass-current — a 2px border —
   because it occupies the same place in the strip and answers the same
   question ("where is it"); only the colour differs. */
.pass-step.pass-failed {
  border-width: 2px;
  border-color: #f5365c;
  background: #fff5f7;
  padding: 5px 7px;
}
.pass-step.pass-failed .pass-step-name {
  color: #f5365c;
}
@media (max-width: 575.98px) {
  .pass-timeline {
    grid-template-columns: 1fr;
  }
}
/*
 * `no_data` — "the switch answered, but nothing was archived for this number".
 *
 * A third colour on purpose. The two it sits between, compiled out of
 * src/assets/sass, are badge-success (#1aae6f on #b0edd3) and badge-danger
 * (#f80031 on #fdd1da): a light tint of a hue with saturated text of the same
 * hue. This follows that idiom in amber, which is also the colour of the
 * "archive is incomplete" alert the operator clicked through from, so the
 * warning and the rows that caused it read as the same thing.
 *
 * Measured, not guessed (sRGB relative luminance for the ratios, CIE76 dE for
 * the distances):
 *
 *   text on fill      5.55:1   — clears AA, and is the HIGHEST-contrast of the
 *                                three badges: badge-success is 2.17:1 and
 *                                badge-danger 3.04:1 on their own fills, so the
 *                                new state is the most legible, not the least
 *   fill vs #b0edd3   dE 31.2  (success fill)
 *   fill vs #fdd1da   dE 24.0  (danger fill)
 *   text vs #1aae6f   dE 89.1  (success text)
 *   text vs #f80031   dE 49.9  (danger text)
 *   fill vs #ffffff   dE 22.5  — reads as a pill on the card, not as bare text
 *
 * Far enough from both neighbours, in fill AND in text, that no two of the
 * three collapse together.
 */
.badge-no-data {
  color: #94430b;
  background-color: #fde4c0;
}
.vm-error-detail {
  background: #fff5f7;
  border: 1px solid #f5365c;
  border-radius: 4px;
  padding: 10px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 60vh;
  overflow-y: auto;
  margin: 0;
}
</style>
